import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Link } from 'react-router-dom';
import { User, Building2, Shield, Users as UsersIcon, ChevronRight } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const { currentCompany, companies } = useCompany();
  const isAdmin = (user || {}).role === 'admin';

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account and workspace</p>
      </div>

      {/* Admin: Users link */}
      {isAdmin && (
        <Link
          to="/settings/users"
          className="block bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition"
          data-testid="settings-users-link"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <UsersIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Users & Permissions</p>
                <p className="text-xs text-gray-500 mt-0.5">Add team members and control what they see</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
        </Link>
      )}

      {/* Profile */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
            <User className="w-5 h-5 text-gray-600" />
          </div>
          <h2 className="font-medium text-gray-900">Profile</h2>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Name</span>
            <span className="text-sm font-medium text-gray-900" data-testid="settings-name">{user?.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">Username</span>
            <span className="text-sm font-medium text-gray-900" data-testid="settings-username">@{user?.username}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-sm text-gray-500">Role</span>
            <span className="text-sm font-medium text-gray-900 capitalize flex items-center gap-1.5" data-testid="settings-role">
              <Shield className="w-3.5 h-3.5" />
              {user?.role}
            </span>
          </div>
        </div>
      </div>

      {/* Companies */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-gray-600" />
          </div>
          <h2 className="font-medium text-gray-900">Companies</h2>
        </div>
        <div className="space-y-2">
          {companies.map((company) => (
            <div key={company.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0" data-testid={`settings-company-${company.slug}`}>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: company.color }} />
              <div>
                <p className="text-sm font-medium text-gray-900">{company.name}</p>
                {company.description && <p className="text-xs text-gray-500">{company.description}</p>}
              </div>
              {currentCompany?.id === company.id && (
                <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Active</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
